import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { InputField } from "@/components/InputField/InputField";
import { ButtonColor } from "@/components/Button/types";
import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";
import {
	MultiAutoCompleteApi,
	MultiAutoCompleteApiItem,
} from "@/components/MultiAutoCompleteApi/MultiAutoCompleteApi";
import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";
import {
	useGetGeoCountriesMutation,
	useLazyGetCompaniesAutocompleteQuery,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";
import { useLazyGlobalSearchQuery } from "@/pages/Private/redux/search/search.api";
import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";
import { searchSelector } from "@/pages/Private/redux/search/search.slice";
import { useAppSelector } from "@/redux/hooks";

import { ByFiltersTabResults } from "./ByFiltersTabResults";
import { ByFiltersAdvancedSection } from "./ByFiltersAdvancedSection";
import { ReferralSearchContext, SingleAddRequest } from "./referralContext";
import { ButtonSize } from "../DropdownButton/types";

interface ByFiltersTabProps {
	selectedLinkedInItems: {
		linkedinUrl: string;
		name: string;
		domain?: string;
		companyEnrichId?: number;
	}[];
	setSelectedLinkedInItems: (
		items: { linkedinUrl: string; name: string; domain?: string; companyEnrichId?: number }[]
	) => void;
	selectedIds: number[];
	initialCompany?: { name: string; domain: string };
	selectedContactIds: number[];
	setSelectedContactIds: (ids: number[]) => void;
	onContactNavigate: (item: ListPersonGlobalSearchInfoDTO) => void;
	// Single-select (compose) mode hides the "Select all" bulk controls in the results panel.
	singleSelect?: boolean;
	// Wrong-person referral flow: prefill company/department/country/name from the reply
	// context and show the extra name field (see referralContext.ts).
	referralContext?: ReferralSearchContext;
	// When set, every result row gets an "Add to outreach" button that hands the single
	// row up to the modal (which opens the list picker for it).
	onAddSingleToList?: (request: SingleAddRequest) => void;
}

export const ByFiltersTab: FunctionComponent<ByFiltersTabProps> = ({
	selectedLinkedInItems,
	setSelectedLinkedInItems,
	selectedIds,
	initialCompany,
	selectedContactIds,
	setSelectedContactIds,
	onContactNavigate,
	singleSelect = false,
	referralContext,
	onAddSingleToList,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	// ── Filter state ──────────────────────────────────────────────────────────
	const prefilledCompany = initialCompany ?? referralContext?.company;
	const [companies, setCompanies] = useState<string[]>(() =>
		prefilledCompany ? [prefilledCompany.domain] : []
	);
	const [positionQuery, setPositionQuery] = useState<string[]>([]);
	const [seniority, setSeniority] = useState<string[]>([]);
	const [department, setDepartment] = useState<string[]>(() =>
		referralContext?.department ? [referralContext.department] : []
	);
	const [countries, setCountries] = useState<{ id: string; name: string }[]>([]);
	const [universities, setUniversities] = useState<string[]>([]);
	// Person-name query (wrong-person flow only): shown as an extra field when the referral
	// context carries an extracted name; sent as personSearchSource.query.
	const [nameQuery, setNameQuery] = useState(referralContext?.nameQuery ?? "");
	const showNameField = referralContext?.nameQuery !== undefined;
	const [companyCountries, setCompanyCountries] = useState<{ id: string; name: string }[]>([]);
	const [companyStates, setCompanyStates] = useState<{ id: number; name: string }[]>([]);
	const [companyCities, setCompanyCities] = useState<{ id: number; name: string }[]>([]);

	// ── Result state ──────────────────────────────────────────────────────────
	const { contacts, companyEnrich } = useAppSelector(searchSelector);
	const contactResults = contacts.data;
	const linkedInResults = companyEnrich.items;
	const [hasSearched, setHasSearched] = useState(false);

	// ── Queries ───────────────────────────────────────────────────────────────
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const [triggerGetCompaniesAutocomplete] = useLazyGetCompaniesAutocompleteQuery();
	const [triggerGetSuggestion] = useLazyGeSuggestionQuery();
	const [fetchSearch, { isFetching: isSearching }] = useLazyGlobalSearchQuery();

	// ── Referral country prefill ──────────────────────────────────────────────
	// The lead's organization only carries a plain country name; resolve it to a geo entry
	// once on mount and prefill the person-country filter on an exact (case-insensitive)
	// name match — anything fuzzier risks searching the wrong country, so we skip instead.
	const referralCountryName = referralContext?.countryName;

	useEffect(() => {
		if (!referralCountryName) {
			return;
		}

		let cancelled = false;

		getGeoCountries({ query: referralCountryName })
			.unwrap()
			.then((result) => {
				const match = result.items.find(
					(item) => item.name.toLowerCase() === referralCountryName.toLowerCase()
				);

				if (match && !cancelled) {
					setCountries([{ id: match.code, name: match.name }]);
				}
			})
			.catch(() => undefined);

		return () => {
			cancelled = true;
		};
	}, [referralCountryName, getGeoCountries]);

	// ── Autocomplete fetchers ─────────────────────────────────────────────────
	const fetchCountries = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	const fetchCompanyDomains = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await triggerGetCompaniesAutocomplete({ query }).unwrap();

			return result.map((item) => ({
				id: item.domain,
				title: item.name ? `${item.domain} (${item.name})` : item.domain,
				logoUrl: item.logoUrl,
			}));
		},
		[triggerGetCompaniesAutocomplete]
	);

	// LinkedIn school suggestions; the education filter stores the institution *name*
	// (free-text query upstream), so the title doubles as the item id.
	const fetchSchools = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await triggerGetSuggestion({ type: "school", keyword: query }).unwrap();
			const titles = [...new Set(result.suggestions.map((suggestion) => suggestion.title))];

			return titles.map((title) => ({ id: title, title }));
		},
		[triggerGetSuggestion]
	);

	// ── Search ────────────────────────────────────────────────────────────────
	const handleSearch = async () => {
		try {
			await fetchSearch({
				personSearchSource: {
					query: showNameField && nameQuery.trim().length > 0 ? nameQuery.trim() : undefined,
					domains: companies.length > 0 ? companies : undefined,
					positionQuery: positionQuery.length > 0 ? positionQuery : undefined,
					seniority: seniority.length > 0 ? seniority : undefined,
					department: department.length > 0 ? department : undefined,
					countries: countries.length > 0 ? countries : undefined,
					education: universities.length > 0 ? { institutionNameQuery: universities } : undefined,
					companyCountries: companyCountries.length > 0 ? companyCountries : undefined,
					companyStates: companyStates.length > 0 ? companyStates : undefined,
					companyCities: companyCities.length > 0 ? companyCities : undefined,
				},
			}).unwrap();
		} catch (e) {
			console.error("Search failed:", e);
		} finally {
			setHasSearched(true);
		}
	};

	return (
		<div className="flex min-h-[320px] h-[480px] max-h-[480px]">
			{/* ── Left column — filters ── */}
			<div className="w-[320px] shrink-0 border-r border-gray-100 flex flex-col">
				<div className="flex-1 overflow-y-auto bb-scrollbar-darker p-4 flex flex-col gap-4">
					{showNameField && (
						<InputField
							label={t("inbox.thread.wrongPerson.nameFilterLabel")}
							name="referral-name-query"
							testId="referral-name-query"
							value={nameQuery}
							handleChange={(e) => setNameQuery(e.target.value)}
						/>
					)}
					<CompanyEnrichAutoComplete
						fetchFn={fetchCompanyDomains}
						handleSelect={(value) => setCompanies(value?.map((item) => item.id) || [])}
						label={ts("companyDomainsLabel")}
						placeholder={ts("domainsPlaceholder")}
						selectedItems={companies.map((domain) => ({ id: domain, title: domain }))}
						testId="autocomplete-company-name"
					/>

					<MultiAutoCompleteApi
						stayOn
						handleSelect={(value?: MultiAutoCompleteApiItem[]) =>
							setPositionQuery(value?.map((r) => r.id.toString()) || [])
						}
						label={ts("positionQueryLabel")}
						placeholder={ts("positionQueryPlaceholder")}
						propertyName="title"
						selectedItems={positionQuery.map((r) => ({ id: r, title: r }))}
					/>

					<MultiAutoComplete
						stayOn
						data={SENIORITY_OPTIONS}
						handleSelect={(value) => setSeniority(value?.map((r) => r.id.toString()) || [])}
						label={ts("seniorityLabel")}
						placeholder={ts("seniorityPlaceholder")}
						selectedItems={seniority.map((r) => ({
							id: r,
							title: SENIORITY_OPTIONS.find((o) => o.id === r)?.title ?? r,
						}))}
					/>

					<MultiAutoComplete
						stayOn
						data={DEPARTMENT_OPTIONS}
						handleSelect={(value) => setDepartment(value?.map((r) => r.id.toString()) || [])}
						label={ts("departmentLabel")}
						placeholder={ts("departmentPlaceholder")}
						selectedItems={department.map((r) => ({
							id: r,
							title: DEPARTMENT_OPTIONS.find((o) => o.id === r)?.title ?? r,
						}))}
					/>

					<CompanyEnrichAutoComplete
						stayOn
						fetchFn={fetchCountries}
						handleSelect={(value) =>
							setCountries(value?.map((r) => ({ id: r.id, name: r.title })) || [])
						}
						label={ts("countriesLabel")}
						placeholder={ts("countriesPlaceholder")}
						selectedItems={countries.map((c) => ({ id: c.id, title: c.name }))}
					/>

					<CompanyEnrichAutoComplete
						stayOn
						allowCustomValues
						fetchFn={fetchSchools}
						handleSelect={(value) => setUniversities(value?.map((r) => r.id) || [])}
						label={ts("universitiesLabel")}
						placeholder={ts("universitiesPlaceholder")}
						selectedItems={universities.map((name) => ({ id: name, title: name }))}
						testId="autocomplete-universities"
					/>

					<ByFiltersAdvancedSection
						companyCountries={companyCountries}
						setCompanyCountries={setCompanyCountries}
						companyStates={companyStates}
						setCompanyStates={setCompanyStates}
						companyCities={companyCities}
						setCompanyCities={setCompanyCities}
					/>
				</div>

				<div className="shrink-0 p-3 ">
					<Button
						color={ButtonColor.ACTION}
						isLoading={isSearching}
						title={ts("search")}
						className="w-full"
						size={ButtonSize.S}
						onClick={async (e) => {
							e.preventDefault();
							e.stopPropagation();
							await handleSearch();
						}}
					/>
				</div>
			</div>

			{/* ── Right column — results ── */}
			<div className="flex-1 flex flex-col overflow-y-auto bb-scrollbar-darker">
				<ByFiltersTabResults
					hasSearched={hasSearched}
					isSearching={isSearching}
					contactResults={contactResults}
					linkedInResults={linkedInResults}
					selectedIds={selectedIds}
					selectedContactIds={selectedContactIds}
					setSelectedContactIds={setSelectedContactIds}
					onContactNavigate={onContactNavigate}
					selectedLinkedInItems={selectedLinkedInItems}
					setSelectedLinkedInItems={setSelectedLinkedInItems}
					singleSelect={singleSelect}
					onAddSingleToList={onAddSingleToList}
				/>
			</div>
		</div>
	);
};
